/* eslint-disable camelcase */
import { DateTime } from "luxon"
import { IntlShape } from "react-intl"
import { formatDateFRstring } from "./date"

export type FormStatus =
  | "deactivated"
  | "draft"
  | "kyc_pending"
  | "waiting_signature"
  | "rejected"
  | "signed_declaration_ongoing"
  | "declared"
  | "transferred"
  | "error"

export interface PaForm {
  id: number
  status: FormStatus
  company_id: number
  company_name: string
  identification_number: string
  created_at: string
  creator_email: string
  is_form_editable?: boolean
  can_resend_signature?: boolean
}

export type DocumentType = "mandate" | "formal_agreement"
export type DocumentStatus =
  | "draft"
  | "ongoing"
  | "done"
  | "deleted"
  | "expired"
  | "canceled"
  | "approval"
  | "rejected"
  | "declined"
export type DocumentSignerStatus =
  | "initiated"
  | "notified"
  | "consent_given"
  | "verified"
  | "processing"
  | "signed"
  | "error"
  | "aborted"
  | "declined"
  | "signature_not_needed"

export interface PaDocument {
  id: number
  document_id: string
  document_type: DocumentType
  status: DocumentStatus
  accountant_signer_status: DocumentSignerStatus
  accountant_signed_at: string
  accountant_declined_at: string
  customer_signer_status: DocumentSignerStatus
  customer_signed_at: string
  customer_declined_at: string
  accountant_error_email_status?: string | null
  accountant_error_email_message?: string | null
  customer_error_email_status?: string | null
  customer_error_email_message?: string | null
}

export const getSignerEmailError = (
  document: PaDocument,
  signer: "accountant" | "customer"
): string | null => {
  const status = document[`${signer}_error_email_status`]
  const message = document[`${signer}_error_email_message`]
  if (!status && !message) return null

  return `Email non délivré${status ? ` (${status})` : ""}${
    message ? ` : ${message}` : ""
  }`
}

export interface Executive {
  full_name: string
  last_name: string
  first_name: string
  usual_first_name: string
}

export interface SiretInfo {
  siret: string
  address_1: string
  address_2: string
  address_3: string
  city: string
  country_code: string
  postal_code: string
}

export type PaSuffixeStatus =
  | "draft"
  | "pending"
  | "signed"
  | "declared"
  | "transfer_scheduled"
  | "transferred"
  | "error"
export interface PaSuffixe {
  status: PaSuffixeStatus
  date_start: string
  date_end: string
  suffixe: string
  email: string
  error_message: string
  lost_reason?: PaLostReason | null
  transfer_effective_date?: string | null
}

export type PaLostReason = "TAKEN_BY_PA" | "TAKEN_BY_SC"

/**
 * Recalage à l'ouverture sur J+9 (`minimalDateStart`, YYYY-MM-DD, fourni par le back) :
 * - un form draft/rejected/error reste relançable : ses suffixes draft/error ;
 * - un suffixe en erreur (refusé par la PA) est relancé quel que soit le form, et le
 *   back revalide sa date.
 * Un suffixe draft d'un form en cours garde la date déjà transmise à la PA.
 */
export const realignSuffixesDateStart = (
  suffixes: PaSuffixe[],
  formStatus: FormStatus,
  minimalDateStart: string
): PaSuffixe[] => {
  if (["draft", "rejected", "error"].includes(formStatus)) {
    return realignDraftSuffixesDateStart(suffixes, minimalDateStart)
  }

  return suffixes.map((s) =>
    s.status === "error" ? realignLineDateStart(s, minimalDateStart) : s
  )
}

/**
 * Recale les lignes que le back (re)demandera, quel que soit le statut du form :
 * c'est le périmètre de sa validation J+9 (utilisé après un refus J+9).
 */
export const realignDraftSuffixesDateStart = (
  suffixes: PaSuffixe[],
  minimalDateStart: string
): PaSuffixe[] =>
  suffixes.map((s) =>
    ["draft", "error"].includes(s.status)
      ? realignLineDateStart(s, minimalDateStart)
      : s
  )

// Lignes du formulaire enregistré recalées : ouverture et annulation.
export const realignFormLines = (
  form: Pick<FormHistory, "status" | "suffixes" | "routage_codes">,
  minimalDateStart: string
) => ({
  suffixes: realignSuffixesDateStart(
    form.suffixes,
    form.status,
    minimalDateStart
  ),
  routageCodes: realignRoutageCodesDateStart(
    form.routage_codes,
    minimalDateStart
  ),
})

export const realignRoutageCodesDateStart = (
  routageCodes: PaCodeRoutage[],
  minimalDateStart: string
): PaCodeRoutage[] =>
  routageCodes.map((r) => realignLineDateStart(r, minimalDateStart))

/**
 * Recale une ligne (suffixe, code routage, onglet de saisie) sur J+9. La date de fin
 * devenue antérieure est vidée : le back refuserait une fin avant le début.
 */
export const realignLineDateStart = <
  T extends { date_start: string; date_end: string }
>(
  line: T,
  minimalDateStart: string
): T => {
  if (line.date_start && line.date_start >= minimalDateStart) return line

  return {
    ...line,
    date_start: minimalDateStart,
    date_end:
      line.date_end && line.date_end < minimalDateStart ? "" : line.date_end,
  }
}

/**
 * J+9 renvoyé par le back. Repli approximatif s'il manque (front déployé avant le
 * back) : jour UTC + 9, qui suppose un serveur en UTC (conteneurs sans TZ, comme
 * PaSuffix.minimal_date_start) ; un refus J+9 du back recale ensuite sur sa date.
 */
export const minimalDateStartOf = (
  company?: { minimal_date_start?: string } | null
): string =>
  company?.minimal_date_start ||
  DateTime.utc().startOf("day").plus({ day: 9 }).toFormat("yyyy-MM-dd")

// Code renvoyé par save_pa_form quand une date de début est sous J+9.
export const PA_DATE_START_BEFORE_MINIMAL = "date_start_before_minimal"

export interface PaCodeRoutage extends SiretInfo {
  date_start: string
  date_end: string
  routage_code: string
  email: string
  service_name: string
}

export interface FormHistory {
  id: number
  status: FormStatus
  signer_customer_user_email: string
  signer_accountant_user_id: number
  signer_customer_user_id: number
  customer_signer_email: string | null
  suffixes: PaSuffixe[]
  routage_codes: PaCodeRoutage[]
  documents: PaDocument[]
  created_at: string
  executive_accountant_first_name: string
  executive_accountant_last_name: string
  executive_customer_first_name: string
  executive_customer_last_name: string
  skip_mandate_creation: boolean
  skip_mandate_legal_confirmation: boolean
  accountant_will_sign_formal_agreement: boolean
  uploaded_mandate_file_name: string | null
  uploaded_mandate_get_url: string | null
  is_form_editable: boolean
  can_resend_signature?: boolean
}

export interface Customer {
  id: number
  email: string
}

export interface CompanyWithoutPa {
  id: number
  company_name: string
  identification_number: string
  email_capture: string
  default_suffixe?: string
  siren_taken_suffixes?: string[]
  // J+9 calculé par le back (YYYY-MM-DD) : l'horloge du navigateur peut être en
  // retard (onglet ouvert la veille, fuseau DOM-TOM). Lu via minimalDateStartOf.
  minimal_date_start?: string
}

// Ligne d'annuaire sur le SIREN seul (suffixe vide côté API).
export const NAKED_SIREN_SUFFIXE = ""

export const isNakedSiren = (suffixe: Pick<PaSuffixe, "suffixe">): boolean =>
  !suffixe.suffixe

export const isSuffixeTaken = (
  suffixe: string,
  takenSuffixes: string[] | undefined
): boolean => (takenSuffixes || []).includes(suffixe)

export const buildInitialSuffixes = (company: {
  default_suffixe?: string
  email_capture: string | null
  siren_taken_suffixes?: string[]
  minimal_date_start?: string
}): PaSuffixe[] =>
  [NAKED_SIREN_SUFFIXE, company.default_suffixe || "CT"]
    .filter((value) => !isSuffixeTaken(value, company.siren_taken_suffixes))
    .map((value) => ({
      status: "draft",
      date_start: minimalDateStartOf(company),
      date_end: "",
      suffixe: value,
      email: company.email_capture || "",
      error_message: "",
    }))

export const getNextDefaultSuffixe = (
  defaultSuffixe: string | undefined,
  existingSuffixes: PaSuffixe[],
  takenSuffixes: string[] | undefined
): string => {
  const candidate = defaultSuffixe || "CT"
  if (existingSuffixes.some((s) => s.suffixe === candidate)) return ""
  if (isSuffixeTaken(candidate, takenSuffixes)) return ""
  return candidate
}

export interface PaMaintenance {
  maintenance_type: "ppf"
  start_date: string
  end_date: string
}

type ColorByStatusReturnType =
  | "amaranth"
  | "brightSun"
  | "shamrock"
  | "orange"
  | "columbia"
  | "cornflower"

export const PaFormColorByStatus = (status: FormStatus) => {
  const statuses: Record<FormStatus, ColorByStatusReturnType> = {
    deactivated: "amaranth",
    draft: "brightSun",
    kyc_pending: "columbia",
    waiting_signature: "orange",
    signed_declaration_ongoing: "cornflower",
    declared: "shamrock",
    transferred: "amaranth",
    error: "amaranth",
    rejected: "amaranth",
  }

  return statuses[status] || "shamrock"
}

export const ColorBySignerStatus = (status: DocumentSignerStatus) => {
  const statuses: Record<DocumentSignerStatus, ColorByStatusReturnType> = {
    initiated: "orange",
    notified: "brightSun",
    consent_given: "brightSun",
    verified: "brightSun",
    processing: "brightSun",
    signed: "shamrock",
    error: "amaranth",
    aborted: "amaranth",
    declined: "amaranth",
    signature_not_needed: "shamrock",
  }
  return statuses[status] || "brightSun"
}

export const ColorByDocumentStatus = (status: DocumentStatus) => {
  const statuses: Record<DocumentStatus, ColorByStatusReturnType> = {
    draft: "brightSun",
    ongoing: "orange",
    done: "shamrock",
    deleted: "amaranth",
    canceled: "amaranth",
    approval: "brightSun",
    rejected: "amaranth",
    declined: "amaranth",
    expired: "amaranth",
  }
  return statuses[status] || "brightSun"
}

export const paSuffixeStatusLabel = (suffixe: PaSuffixe, intl: IntlShape) => {
  const label = intl.formatMessage(
    { id: `dashboard-pa.preview.suffixe-table.status.${suffixe.status}` },
    { date: formatDateFRstring(suffixe.transfer_effective_date || "") }
  )
  if (!suffixe.lost_reason) return label

  const reason = intl.formatMessage({
    id: `dashboard-pa.preview.suffixe-table.lost-reason.${suffixe.lost_reason}`,
  })
  return `${label} (${reason})`
}

export const paFormStatusLabel = (
  form: { status: FormStatus; suffixes?: PaSuffixe[] } | null | undefined,
  intl: IntlShape
) => {
  if (!form)
    return intl.formatMessage({ id: "fiduciary.pa.table-body.status.null" })
  if (form.status !== "transferred")
    return intl.formatMessage({
      id: `fiduciary.pa.table-body.status.${form.status}`,
    })

  const transferDate = (form.suffixes || [])
    .map((suffixe) => suffixe.transfer_effective_date)
    .filter((date): date is string => Boolean(date))
    .sort()
    .pop()

  return intl.formatMessage(
    {
      id: transferDate
        ? "fiduciary.pa.table-body.status.transferred.with-date"
        : "fiduciary.pa.table-body.status.transferred",
    },
    { date: formatDateFRstring(transferDate || "") }
  )
}

export const PaSuffixeColorByStatus = (status: PaSuffixeStatus) => {
  const statuses: Record<PaSuffixeStatus, ColorByStatusReturnType> = {
    draft: "brightSun",
    pending: "orange",
    signed: "cornflower",
    declared: "shamrock",
    transfer_scheduled: "orange",
    transferred: "amaranth",
    error: "amaranth",
  }
  return statuses[status] || "brightSun"
}

export const isPpfSirenNotFoundError = (error: unknown): boolean => {
  if (!error) return false
  const raw = typeof error === "string" ? error : JSON.stringify(error)
  const normalized = raw.toLowerCase()
  return normalized.includes("ppf") && normalized.includes("not found")
}

const STATUS_CONFLICT_MARKERS = [
  "ne peut pas être modifié",
  "cannot be canceled",
  "non annulable",
]

export const isStatusConflictError = (raw: string): boolean => {
  const normalized = raw.toLowerCase()
  return STATUS_CONFLICT_MARKERS.some((marker) => normalized.includes(marker))
}

export const BUYER_ADDRESSING_ERROR_PREFIX =
  "Adresse de facturation électronique"

export const isBuyerAddressingError = (errorMessage?: string | null): boolean =>
  Boolean(errorMessage?.startsWith(BUYER_ADDRESSING_ERROR_PREFIX))

export const getPaErrorMessage = (
  rawErrorMessage: string | string[] | undefined | null,
  intl: IntlShape,
  options?: { withStatusConflictPrefix?: boolean }
): string => {
  if (isPpfSirenNotFoundError(rawErrorMessage)) {
    return intl.formatMessage({ id: "dashboard-pa.ppf-siren-not-found" })
  }

  const raw = Array.isArray(rawErrorMessage)
    ? rawErrorMessage.filter(Boolean).join("\n")
    : typeof rawErrorMessage === "string"
    ? rawErrorMessage
    : rawErrorMessage
    ? JSON.stringify(rawErrorMessage)
    : ""

  // Le préfixe « statut » ne vaut que pour un vrai conflit de statut : sur une erreur
  // PA (suffixe déjà pris, email invalide…) il masquait la vraie cause.
  if (
    !options?.withStatusConflictPrefix ||
    (raw && !isStatusConflictError(raw))
  ) {
    return raw
  }

  const generic = intl.formatMessage({
    id: "dashboard-pa.create.error.status-conflict",
  })
  if (!raw) return generic
  return `${generic}\n${raw}`
}

export const filterPaForms = (pdpForms: PaForm[], search: string) => {
  if (!search) {
    return pdpForms
  }

  return pdpForms.filter((form) => {
    return (
      form.company_name.toLowerCase().includes(search.toLowerCase()) ||
      form.identification_number.toLowerCase().includes(search.toLowerCase()) ||
      form.creator_email.toLowerCase().includes(search.toLowerCase())
    )
  })
}

export const sortPaFormsByCompany = (pdpForms: PaForm[], asc: boolean) => {
  return [
    ...pdpForms.sort((a, b) =>
      asc
        ? a.company_name.localeCompare(b.company_name)
        : b.company_name.localeCompare(a.company_name)
    ),
  ]
}

export const sortPaFormsByAccountant = (pdpForms: PaForm[], asc: boolean) => {
  return [
    ...pdpForms.sort((a, b) =>
      asc
        ? a.creator_email.localeCompare(b.creator_email)
        : b.creator_email.localeCompare(a.creator_email)
    ),
  ]
}

export const sortPaFormsByStatus = (
  pdpForms: PaForm[],
  asc: boolean,
  intl: IntlShape
) => {
  return pdpForms.sort((a, b) => {
    const aLabel = intl.formatMessage({
      id: `fiduciary.pa.table-body.status.${a.status ?? "null"}`,
    })
    const bLabel = intl.formatMessage({
      id: `fiduciary.pa.table-body.status.${b.status ?? "null"}`,
    })
    return asc ? aLabel?.localeCompare(bLabel) : bLabel?.localeCompare(aLabel)
  })
}

export const PA_DOCUMENT_INVALID_STATUSES: DocumentStatus[] = [
  "rejected",
  "deleted",
  "expired",
  "canceled",
  "declined",
]

export const PA_DOCUMENT_SIGNER_INVALID_STATUSES: DocumentSignerStatus[] = [
  "declined",
  "aborted",
  "error",
]

export const has_signed_or_pending_mandate = (paDocuments: PaDocument[]) => {
  return paDocuments.some(
    (document) =>
      document.document_type === "mandate" &&
      !PA_DOCUMENT_INVALID_STATUSES.includes(document.status) &&
      !PA_DOCUMENT_SIGNER_INVALID_STATUSES.includes(
        document.accountant_signer_status
      ) &&
      !PA_DOCUMENT_SIGNER_INVALID_STATUSES.includes(
        document.customer_signer_status
      )
  )
}

export const has_uploaded_mandate = (
  form: Pick<
    FormHistory,
    "skip_mandate_legal_confirmation" | "uploaded_mandate_file_name"
  >
): boolean => {
  return Boolean(
    form.skip_mandate_legal_confirmation && form.uploaded_mandate_file_name
  )
}

export const has_valid_mandate_for_resubmit = (form: FormHistory): boolean => {
  return (
    has_signed_or_pending_mandate(form.documents) || has_uploaded_mandate(form)
  )
}

export const has_invalid_formal_agreement = (
  paDocuments: PaDocument[],
  accountantWillSignFormalAgreement: boolean
): boolean => {
  const formalAgreements = paDocuments.filter(
    (document) => document.document_type === "formal_agreement"
  )

  if (formalAgreements.length === 0) return false

  return formalAgreements.some((document) => {
    if (PA_DOCUMENT_INVALID_STATUSES.includes(document.status)) return true

    if (accountantWillSignFormalAgreement) {
      return PA_DOCUMENT_SIGNER_INVALID_STATUSES.includes(
        document.accountant_signer_status
      )
    }

    return PA_DOCUMENT_SIGNER_INVALID_STATUSES.includes(
      document.customer_signer_status
    )
  })
}

export const can_resubmit_pa_form = (form: FormHistory | null): boolean => {
  if (!form) return false

  // Relance possible quand :
  // - mandat Yousign signé/en cours OU mandat déposé par l'EC (skip mandat)
  // - accord formel invalide
  // - statut rejected/error
  if (!["rejected", "error"].includes(form.status)) return false
  if (!has_valid_mandate_for_resubmit(form)) return false

  return has_invalid_formal_agreement(
    form.documents,
    form.accountant_will_sign_formal_agreement ?? false
  )
}

export const has_expired_document = (paDocuments: PaDocument[]): boolean => {
  return paDocuments.some((document) => document.status === "expired")
}

// Document expiré (mandat ou accord formel) : on peut relancer sans
// modification du formulaire, si le back le considère éditable.
export const can_regenerate_expired_document = (
  form: FormHistory | null
): boolean => {
  if (!form) return false
  if (!form.is_form_editable) return false
  if (!["rejected", "error"].includes(form.status)) return false
  return has_expired_document(form.documents)
}

// Suffixe en erreur (ligne refusée par la PA, accord formel non signé…) : relançable
// tel quel, sans devoir modifier le formulaire.
export const can_relaunch_error_suffixes = (
  form: FormHistory | null
): boolean => {
  if (!form) return false
  if (!form.is_form_editable) return false
  return form.suffixes.some((s) => s.status === "error")
}
